import lodash, { isEmpty, pick } from 'lodash';
import { replace } from 'react-router-redux';

import { toQuery, toMutation, nodeWithArgs, node } from 'common/util/queryHelper';
import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { addApplicationMessages, addApplicationMessage } from 'common/actions/applicationMessagesActions';

export const ADMIN_LOAD_INSTITUTIONS = 'ADMIN_LOAD_INSTITUTIONS';
export const ADMIN_LOAD_INSTITUTION = 'ADMIN_LOAD_INSTITUTION';
export const ADMIN_CREATE_INSTITUTION = 'ADMIN_CREATE_INSTITUTION';
export const ADMIN_UPDATE_INSTITUTION = 'ADMIN_UPDATE_INSTITUTION';
export const ADMIN_LOAD_TIMEZONES = 'ADMIN_LOAD_TIMEZONES';
export const ADMIN_VALUE_ONCHANGE = 'VALUE_ONCHANGE';
export const ADMIN_INSTITUTION_PROPERTY_ON_CHANGE = 'INSTITUTION_PROPERTY_ON_CHANGE';
export const BINDER_DATASOURCE_OVERRIDES_SET_AVAILABLE_OPTIONS = 'BINDER_DATASOURCE_OVERRIDES_SET_AVAILABLE_OPTIONS';
export const BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS = 'BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS';

export function loadInstitutions() {
    const query = toQuery(node(
        'institutions', [
            'id',
            'status',
            'displayName',
            'documentTypeCount',
            'descriptorCount',
            'userCount',
            'groupCount',
            'binderTemplateCount',
            node('properties', [
                'type',
                'value',
            ])
        ]));

    return dispatch => {
        return queryGraphQL(query).then(results => {
            dispatch(loadInstitutionsAction(results.data.institutions || []));
        });
    };
}

export function loadInstitution(institutionId) {
    const query = toQuery(nodeWithArgs(
        'institutions',
        { id: institutionId },
        [
            'id',
            'status',
            'displayName',
            'documentTypeCount',
            'descriptorCount',
            'userCount',
            'groupCount',
            'binderTemplateCount',
            'notes',
            node('properties', [
                'type',
                'value',
            ]),
            node('documentTypes', [
                'id',
                'name',
                'displayName',
                'isActive',
                node('descriptors', [
                    'id',
                    'name',
                    'displayName',
                    'dataType',
                    'description',
                    'isActive',
                    'isActiveForDocumentType',
                    'isRequiredForDocumentType',
                    'displayOrder'
                ])
            ]),
            node('descriptors', [
                'id',
                'name',
                'displayName',
                'dataType',
                'isActive',
                node('dataset', [
                    'id',
                    'dataType',
                    'values',
                ])
            ])
        ]
    ));

    return dispatch => {
        return queryGraphQL(query).then(results => {
            dispatch(loadInstitutionAction(results.data.institutions[0]));
        });
    };
}

export function createInstitution(institution) {
    const mutation = toMutation(nodeWithArgs('createInstitution', {
        institution
    }, ['id']));

    return dispatch => {
        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                dispatch(addApplicationMessages('success', ['Institution created successfully.']));
                const institution = response.data.createInstitution;
                dispatch(createInstitutionAction(institution));
                dispatch(replace(`/complion/institutions/${institution.id}`));
            }
        });
    };
}

export function updateInstitution(institution) {
    const id = institution.id;
    const fieldsToUpdate = pick(institution, ['status', 'displayName', 'notes']);
    const institutionProperties = pick(institution, ['properties']);
    const mutation = toMutation(nodeWithArgs('updateInstitution', {
        id: id,
        institution: fieldsToUpdate,
        ...institutionProperties
    }, [
        'id',
        'status',
        'displayName',
        'notes'
    ]));

    return dispatch => {
        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                dispatch(addApplicationMessages('error', results.errors.map(error => error.message)));
            } else {
                const updatedInstitution = results.data.updateInstitution;
                dispatch(updateInstitutionAction(updatedInstitution));
                dispatch(addApplicationMessage('success', ['Successfully updated Institution Details.']));
            }
        });
    };
}

export function getTimezoneList() {
    const query = toQuery(node(
        'getTimezoneList', [
            'name',
            'offset'
        ]));

    return async dispatch => {
        const results = await queryGraphQL(query);
        if (results.errors) {
            dispatch(addApplicationMessages('error', results.errors.map(error => error.message)));
        } else {
            dispatch(getTimezoneListAction(results.data.getTimezoneList));
            return results.data.getTimezoneList;
        }
    };
}

export function loadInstitutionsAction(institutions) {
    return {
        type: ADMIN_LOAD_INSTITUTIONS,
        payload: institutions
    };
}

export function loadInstitutionAction(institution) {
    return {
        type: ADMIN_LOAD_INSTITUTION,
        payload: institution
    };
}

export function createInstitutionAction(institution) {
    return {
        type: ADMIN_CREATE_INSTITUTION,
        payload: institution
    };
}

export function updateInstitutionAction(institution) {
    return {
        type: ADMIN_UPDATE_INSTITUTION,
        payload: institution
    };
}

export function getTimezoneListAction(timezones) {
    return {
        type: ADMIN_LOAD_TIMEZONES,
        payload: timezones
    };
}


export function institutionObjectOnChange(institution, key, value) {
    return {
        type: ADMIN_VALUE_ONCHANGE,
        payload: { institution, key, value }
    };
}

export function institutionPropertyOnChange(institution, properties) {
    return {
        type: ADMIN_INSTITUTION_PROPERTY_ON_CHANGE,
        payload: { properties, institution }
    };
}

export function setAvailableBinderDatasourceOverrides(availableOptions) {
    return dispatch => {
        dispatch({
            type: BINDER_DATASOURCE_OVERRIDES_SET_AVAILABLE_OPTIONS,
            payload: availableOptions
        });
    };
}

export function setSelectedDatasourceOverrides(selected) {
    return dispath => {
        dispath({ 
            type: BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS,
            payload: selected
        });
    };
}

export function updateDatasourceOverrides(id, label, dataSourceName) {
    return (dispatch, getState) => {
        const { selectedDatasourceOverrides = [] } = getState();

        if (id === null) {
            const overrides = selectedDatasourceOverrides.filter(override => override.label !== label);
            dispatch({ 
                type: BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS,
                payload: overrides
            });
        } else {
            const overrides = [...selectedDatasourceOverrides];
            const index = overrides.findIndex(override => override.label === label);
            if (index !== -1) {
                overrides[index] = { dataSourceId: id, label, dataSourceName }; 
            } else {
                overrides.push({ dataSourceId: id, label, dataSourceName });
            }
            dispatch({ 
                type: BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS,
                payload: overrides
            });
        }

        // Calculate available datasources for selection
        const selected = getState().selectedDatasourceOverrides;
        const institutionId = lodash.get(getState(), 'me.institution.id');
        const dataSources = lodash.filter(getState().dataSources, ['institutionId', institutionId]);

        const availableToSelect = dataSources.filter(data => !selected.some(el => el.dataSourceId === data.id));
        dispatch(setAvailableBinderDatasourceOverrides(availableToSelect));
    };
}




export function getDatasourceOverrides(institutionId) {
    if (!institutionId) {
        return;
    }
    const query = toQuery(
        nodeWithArgs(
            'dataSourceOverrides',
            { institutionId },
            [
                node(
                    'overrides',
                    [
                        'dataSourceId',
                        'dataSourceName',
                        'label'
                    ]
                )
            ]
        )
    );

    return async (dispatch, getState) => {
        const results = await queryGraphQL(query);
        const dataSources = lodash.filter(getState().dataSources, ['institutionId', institutionId]);

        if(results.data.dataSourceOverrides && !!results.data.dataSourceOverrides.overrides) {
            const selected = results.data.dataSourceOverrides.overrides;
            dispatch(setSelectedDatasourceOverrides(selected));

            const availableToSelect = dataSources.filter(data => !selected.some(el => el.dataSourceId === data.id));
            dispatch(setAvailableBinderDatasourceOverrides(availableToSelect));
        }
    };
}
export function createDatasourceOverrides(institutionId, overrides, notify = true) {
    const mutation = toMutation(nodeWithArgs('createBinderDatasourceOverrides', {
        institutionId,
        overrides
    }));

    return async dispatch => {
        const results = await mutateGraphQL(mutation);

        if (results.error) {
            dispatch(addApplicationMessages('error', results.errors.map(error => error.message)));
        } else {
            dispatch(getDatasourceOverrides(institutionId));
            if(!notify) return;
            
            if (isEmpty(overrides)) {
                dispatch(addApplicationMessages('success', ['Datasource overrides reset.']));
            } else {
                dispatch(addApplicationMessages('success', ['Datasource overrides applied.']));
            }

        }
    };
}
